import React, { useState, useEffect } from 'react';
import './Profile.css';
import { getCurrentUser } from './auth';

function Profile({ onClose }) {
  const [profile, setProfile] = useState({
    email: '',
    name: '',
    family_name: ''
  });
  const [passwords, setPasswords] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [mfaStatus, setMfaStatus] = useState(null);
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const { session, user } = await getCurrentUser();
      const payload = session.getIdToken().payload;
      setProfile({
        email: payload.email || '',
        name: payload.name || '',
        family_name: payload.family_name || ''
      });

      // Check MFA status
      user.getMFAOptions((err, mfaOptions) => {
        if (err) {
          console.error('MFA check error:', err);
          setMfaStatus('unknown');
        } else {
          setMfaStatus(mfaOptions && mfaOptions.length > 0 ? 'enabled' : 'disabled');
        }
      });
    } catch (err) {
      setError('Failed to load profile');
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    try {
      const { user } = await getCurrentUser();
      
      const attributes = [];
      if (profile.name) attributes.push({ Name: 'name', Value: profile.name });
      if (profile.family_name) attributes.push({ Name: 'family_name', Value: profile.family_name });

      await new Promise((resolve, reject) => {
        user.updateAttributes(attributes, (err, result) => {
          if (err) reject(err);
          else resolve(result);
        });
      });

      setMessage('Profile updated successfully!');
    } catch (err) {
      setError('Failed to update profile: ' + err.message);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    if (passwords.newPassword !== passwords.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwords.newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    try {
      const { user } = await getCurrentUser();
      
      await new Promise((resolve, reject) => {
        user.changePassword(passwords.oldPassword, passwords.newPassword, (err, result) => {
          if (err) reject(err);
          else resolve(result);
        });
      });

      setMessage('Password changed successfully!');
      setPasswords({ oldPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setError('Failed to change password: ' + err.message);
    }
  };

  const handleMfaSetup = async () => {
    setMessage('');
    setError('');
    setShowMfaSetup(true);

    try {
      const { user } = await getCurrentUser();
      
      await new Promise((resolve, reject) => {
        user.associateSoftwareToken({
          onSuccess: (session) => resolve(session),
          onFailure: (err) => reject(err),
          associateSecretCode: (secretCode) => {
            const qrCodeUrl = `otpauth://totp/AircraftTracking:${profile.email}?secret=${secretCode}&issuer=AircraftTracking`;
            setQrCode(qrCodeUrl);
            resolve();
          }
        });
      });
    } catch (err) {
      setError('Failed to setup MFA: ' + err.message);
      setShowMfaSetup(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    try {
      const { user } = await getCurrentUser();
      
      await new Promise((resolve, reject) => {
        user.verifySoftwareToken(verifyCode, 'My TOTP Device', {
          onSuccess: (session) => {
            setMessage('MFA enabled successfully!');
            setMfaStatus('enabled');
            setShowMfaSetup(false);
            setVerifyCode('');
            resolve(session);
          },
          onFailure: (err) => reject(err)
        });
      });
    } catch (err) {
      setError('Invalid verification code: ' + err.message);
    }
  };

  return (
    <div className="profile-overlay" onClick={onClose}>
      <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
        <div className="profile-header">
          <h2>Profile Settings</h2>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        {message && <div className="success-message">{message}</div>}
        {error && <div className="error-message">{error}</div>}

        {mfaStatus === 'disabled' && !showMfaSetup && (
          <div className="mfa-prompt">
            <span className="warning-icon">⚠</span>
            <span>Two-Factor Authentication is not enabled. Enable it for better security.</span>
            <button onClick={handleMfaSetup} className="setup-mfa-btn">Setup MFA</button>
          </div>
        )}

        <form onSubmit={handleProfileUpdate} className="profile-form">
          <h3>Personal Information</h3>
          <div className="form-group">
            <label>Email (cannot be changed)</label>
            <input type="email" value={profile.email} disabled />
          </div>
          <div className="form-group">
            <label>First Name</label>
            <input
              type="text"
              value={profile.name}
              onChange={(e) => setProfile({...profile, name: e.target.value})}
              placeholder="Enter first name"
            />
          </div>
          <div className="form-group">
            <label>Last Name</label>
            <input
              type="text"
              value={profile.family_name}
              onChange={(e) => setProfile({...profile, family_name: e.target.value})}
              placeholder="Enter last name"
            />
          </div>
          <button type="submit" className="save-btn">Save Profile</button>
        </form>

        <form onSubmit={handlePasswordChange} className="profile-form">
          <h3>Change Password</h3>
          <div className="form-group">
            <label>Current Password</label>
            <input
              type="password"
              value={passwords.oldPassword}
              onChange={(e) => setPasswords({...passwords, oldPassword: e.target.value})}
              required
            />
          </div>
          <div className="form-group">
            <label>New Password</label>
            <input
              type="password"
              value={passwords.newPassword}
              onChange={(e) => setPasswords({...passwords, newPassword: e.target.value})}
              required
            />
          </div>
          <div className="form-group">
            <label>Confirm New Password</label>
            <input
              type="password"
              value={passwords.confirmPassword}
              onChange={(e) => setPasswords({...passwords, confirmPassword: e.target.value})}
              required
            />
          </div>
          <button type="submit" className="save-btn">Change Password</button>
        </form>

        <div className="profile-form">
          <h3>Two-Factor Authentication (MFA)</h3>
          {mfaStatus === 'enabled' && (
            <div className="mfa-status enabled">
              <span>✓</span> MFA is enabled
            </div>
          )}
          {showMfaSetup && (
            <div className="mfa-setup">
              <p>Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.):</p>
              {qrCode && (
                <div className="qr-code">
                  <img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrCode)}`} alt="QR Code" />
                </div>
              )}
              <form onSubmit={handleMfaVerify}>
                <div className="form-group">
                  <label>Enter verification code from your app:</label>
                  <input
                    type="text"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value)}
                    placeholder="000000"
                    maxLength="6"
                    required
                  />
                </div>
                <button type="submit" className="save-btn">Verify and Enable MFA</button>
                <button type="button" onClick={() => setShowMfaSetup(false)} className="cancel-btn">Cancel</button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Profile;
